<template>
  <div class="group flex items-center gap-4">
    <div
      v-if="showBadge && total > 0"
      class="truncate rounded-full bg-op-90 px-2 py-1 caption-1 fw-bold <md:invisible <md:absolute <md:scale-0 <md:op-0 <md:duration <md:group-hover:visible <md:group-hover:static <md:group-hover:scale-100 <md:group-hover:op-100"
      :class="[
        classes.badge,
        { 'backdrop-blur-xl': blur },
      ]"
    >
      {{ active + 1 }} / <span :class="classes.badgeSecondary">{{ total }}</span>
    </div>
    <div
      ref="sliderRef"
      :aria-hidden="total <= 1 || undefined"
      :aria-label="$t.carouselSlide"
      aria-orientation="horizontal"
      :aria-valuemax="total"
      :aria-valuemin="1"
      :aria-valuenow="active + 1"
      :aria-valuetext="replaceAll($t.slideOf, { current: active + 1, total })"
      class="relative max-w-full min-h-6 min-w-6 flex touch-none select-none items-center rounded-full outline-offset-2 focus-visible:outline-auto"
      :class="[
        brandClasses.trackLayout,
        classes.track,
        { wrap: showAllDots },
        { 'backdrop-blur-xl': blur },
        { 'cursor-pointer': total > 1 },
      ]"
      :role="total > 1 ? 'slider' : undefined"
      :style="edgeFadeMode === 'mask' ? { maskImage: edgeMask } : undefined"
      :tabindex="total > 1 ? 0 : undefined"
      @click="onTrackClick"
      @keydown="onTrackKeydown"
      @pointercancel="onDocumentPointerEnd"
      @pointerdown="onPointerDown"
    >
      <span
        v-for="i in total"
        :key="i"
        aria-hidden="true"
        class="flex center overflow-hidden duration"
        :class="{ [brandClasses.visibleDot]: isInView(i - 1) }"
      >
        <span
          class="duration"
          :class="[
            brandClasses.dotShape,
            brandClasses.dot,
            {
              'relative overflow-hidden': autoplay && i - 1 === active,
              [i - 1 === active ? brandClasses.activeDotSize : brandClasses.inactiveDot]: isInView(i - 1),
            },
            i - 1 === active && classes.activeDot,
          ]"
        >
          <span
            v-if="autoplay && i - 1 === active"
            class="absolute left-0 top-0 h-full ease-linear duration"
            :class="[brandClasses.dotShape, classes.progress]"
            :style="{ width: `${progress * 100}%` }"
          />
        </span>
      </span>
      <div
        v-show="edgeFadeMode === 'overlay' && !showAllDots && dots.start > 0"
        class="pointer-events-none absolute inset-y-0 left-0 w-8 from-transparent bg-gradient-to-l"
        :class="classes.gradient"
      />
      <div
        v-show="edgeFadeMode === 'overlay' && !showAllDots && dots.end < total - 1"
        class="pointer-events-none absolute inset-y-0 right-0 w-8 from-transparent bg-gradient-to-r"
        :class="classes.gradient"
      />
    </div>
    <base-button
      v-if="autoplay"
      :aria-label="paused ? $t.videoPlay : $t.videoPause"
      class="rounded-full bg-op-90 p-1 <md:invisible <md:absolute <md:scale-0 <md:op-0 <md:duration <md:group-hover:visible <md:group-hover:static <md:group-hover:scale-100 <md:group-hover:op-100"
      :class="[
        classes.badge,
        { 'backdrop-blur-xl': blur },
      ]"
      @click="paused = !paused"
    >
      <vf-icon :name="paused ? 'play' : 'pause'" />
    </base-button>
  </div>
</template>

<script setup lang="ts">
import type { Responsive } from '#types/common'
import type { ColorSchema } from '#types/design-system'
import { BaseButton } from '#components'

/**
 * Carousel pagination indicator showing at most 5 dots (md) or 4 (xs/sm)
 * unless showAllDots is enabled.
 * All dots are always in the DOM — hidden ones collapse so width/padding transitions
 * handle enter/leave symmetrically with no layout shifts. Transitions use `duration`,
 * which respects prefers-reduced-motion.
 */
const {
  total,
  variant,
  autoplay,
  progress = 0,
  showAllDots = false,
} = defineProps<{
  /** Total number of items */
  total: number
  variant?: ColorSchema | Responsive<ColorSchema>
  blur?: boolean
  /** When true, shows autoplay progress on the active dot and a play/pause control */
  autoplay?: boolean
  /** Autoplay progress for the active slide, 0–1 */
  progress?: number
  /** Show every dot instead of a moving window */
  showAllDots?: boolean
}>()
const { $viewport } = useNuxtApp()
const active = defineModel<number>('active', { required: true })
const paused = defineModel<boolean>('paused')
const sliderRef = useTemplateRef<HTMLDivElement>('sliderRef')
const { brandClasses, edgeFadeMode, showBadge } = useAppConfig().components.vf.carouselPaginationDots

let requestedIndex = active.value
watch(active, (index) => {
  requestedIndex = index
})

const navigateSliderTo = (index: number) => {
  if (total <= 1) return

  const nextIndex = Math.max(0, Math.min(total - 1, index))
  if (nextIndex === requestedIndex) return

  requestedIndex = nextIndex
  active.value = nextIndex
}

const classes = computed(() => {
  const normalizedVariant = normalizeResponsive(variant)

  const resolve = (config: Responsive<Record<ColorSchema, string>>) =>
    Object.entries(normalizedVariant).reduce(
      (acc, [k, v]) => `${acc} ${config[k][v ?? 'light']}`.trim(),
      ''
    )

  return {
    badge: resolve(brandClasses.badge),
    badgeSecondary: resolve(brandClasses.badgeSecondary),
    track: resolve(brandClasses.track),
    activeDot: resolve(brandClasses.activeDot),
    progress: resolve(brandClasses.progress),
    gradient: resolve(brandClasses.gradient),
  }
})

const maxVisible = computed(() => $viewport.md ? 5 : 4)

const dots = computed(() => {
  if (total <= maxVisible.value) return { start: 0, end: total - 1 }

  const sideSlots = maxVisible.value - 1
  const maxLeft = Math.min(active.value, Math.ceil(sideSlots / 2))
  const maxRight = sideSlots - maxLeft
  const rightAvailable = total - 1 - active.value
  const rightCount = Math.min(rightAvailable, maxRight)
  const leftCount = Math.min(active.value, sideSlots - rightCount)

  return { start: active.value - leftCount, end: active.value + rightCount }
})

const edgeMask = computed(() => {
  if (showAllDots) return undefined

  const fadeLeft = dots.value.start > 0
  const fadeRight = dots.value.end < total - 1

  if (fadeLeft && fadeRight)
    return 'linear-gradient(to right, transparent, black 2rem, black calc(100% - 2rem), transparent)'
  if (fadeLeft) return 'linear-gradient(to right, transparent, black 2rem)'
  if (fadeRight) return 'linear-gradient(to left, transparent, black 2rem)'
  return undefined
})

const isInView = (index: number) => {
  if (showAllDots) return true

  const { start, end } = dots.value
  return index >= start && index <= end
}

const onTrackKeydown = (event: KeyboardEvent) => {
  if (total <= 1) return

  let nextIndex = requestedIndex
  if (event.key === 'ArrowRight' || event.key === 'ArrowUp') nextIndex++
  else if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') nextIndex--
  else if (event.key === 'Home') nextIndex = 0
  else if (event.key === 'End') nextIndex = total - 1
  else return

  event.preventDefault()
  navigateSliderTo(nextIndex)
}

const SWIPE_STEP = 10

const pointer = reactive({
  id: null as number | null,
  suppressClick: false,
  startX: 0,
  startY: 0,
})

const indexAt = (clientX: number, clientY: number) => {
  if (!showAllDots) {
    const rect = sliderRef.value?.getBoundingClientRect()
    if (!rect?.width) return active.value

    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width))
    return Math.min(total - 1, Math.round(ratio * (total - 1)))
  }

  let nearestIndex = active.value
  let nearestDistance = Infinity

  Array.from(sliderRef.value?.children ?? []).slice(0, total).forEach((dot, index) => {
    const rect = dot.getBoundingClientRect()
    if (!rect.width || !rect.height) return

    const dx = Math.max(rect.left - clientX, 0, clientX - rect.right)
    const dy = Math.max(rect.top - clientY, 0, clientY - rect.bottom)
    const distance = dx * dx + dy * dy

    if (distance < nearestDistance) {
      nearestIndex = index
      nearestDistance = distance
    }
  })

  return nearestIndex
}

const indexOnClick = (clientX: number, clientY: number) => {
  const activeDot = sliderRef.value?.children[active.value]?.firstElementChild
  const rect = activeDot?.getBoundingClientRect()
  if (!rect) return active.value

  if (clientX < rect.left)
    return showAllDots ? Math.min(active.value - 1, indexAt(clientX, clientY)) : active.value - 1
  if (clientX > rect.right)
    return showAllDots ? Math.max(active.value + 1, indexAt(clientX, clientY)) : active.value + 1
  return active.value
}

const onDocumentPointerMove = (event: PointerEvent) => {
  if (event.pointerId !== pointer.id)
    return

  const distance = Math.hypot(event.clientX - pointer.startX, event.clientY - pointer.startY)

  if (distance < SWIPE_STEP)
    return

  pointer.suppressClick = true
  const index = indexAt(event.clientX, event.clientY)
  navigateSliderTo(index)
}

const onDocumentPointerEnd = (event?: PointerEvent) => {
  if (event && event.pointerId !== pointer.id) return

  pointer.id = null
  document.removeEventListener('pointermove', onDocumentPointerMove)
  document.removeEventListener('pointerup', onDocumentPointerEnd)
  document.removeEventListener('pointercancel', onDocumentPointerEnd)
}

const onPointerDown = (event: PointerEvent) => {
  if (total <= 1 || pointer.id !== null || !event.isPrimary || event.button !== 0) return

  pointer.id = event.pointerId
  pointer.suppressClick = false
  pointer.startX = event.clientX
  pointer.startY = event.clientY

  document.addEventListener('pointermove', onDocumentPointerMove)
  document.addEventListener('pointerup', onDocumentPointerEnd)
  document.addEventListener('pointercancel', onDocumentPointerEnd)
}

onUnmounted(onDocumentPointerEnd)

const onTrackClick = (event: MouseEvent) => {
  if (total <= 1) return
  if (pointer.suppressClick) {
    event.preventDefault()
    pointer.suppressClick = false
    return
  }
  if (event.detail) navigateSliderTo(indexOnClick(event.clientX, event.clientY))
}
</script>
